/**
 * forecastCards.ts — Forecast programme F4a (register row 72): the MODELS of
 * four Forecast-tab cards, pure, from ONE /v1/sales/forecast response. The
 * components (ForecastCards.tsx) render exactly what these return.
 *
 * Spec docs/design/calm-minimal/specs/klaxiora-forecast-spec.md §3.4–§3.6 under
 * docs/design/stier/ADOPTION.md §18 (§18.4 item 2: the measure is named
 * "Categorised share", never "coverage", which the page already owns for the
 * override coverage line; §18.5: the Attribution card is co-equal and ALWAYS
 * present, the spec's hide-at-zero REFUSED).
 *
 * THE RULES OF THIS FILE:
 * - Every money figure is the WIRE's, with the wire's own fence count beside
 *   it (§26 F-1). The only arithmetic made here is RATIOS of wire figures —
 *   the categorised share (§18.10 step 5: "FRONTEND-only once named") and a
 *   month's share of the next period — and bar widths.
 * - The look-ahead is the wire's `nextPeriod`: its title, months, totals and
 *   deals. The period is never hardcoded ("Q4"): from 1 October the default
 *   period is Q4 and the look-ahead is Q1 2027 — on Fourth Coffee an EMPTY
 *   next period, which renders as a true statement about that period, not as
 *   a failure (the 2026-09-30 timing finding, recorded in the build log).
 * - Sentences say only what the data proves. Where the spec's sentence would
 *   be false for some data, the copy is narrowed (each deviation is named at
 *   its function).
 */
import type { SalesFencedFigure, SalesForecast, SalesForecastDealRef, SalesForecastMonth } from '../../../../api/types'
import { money } from '../../../../lib/money'
import { SLUG_TO_STAGE } from '../../api'
import { fmtMoney } from './SalesDashboard'
import { periodName } from './forecastHero'
import { dealCount, fencedDealCount } from './forecastView'
import { excludedSuffix } from './fenceCopy'
import { niceTicks, axisUnit, fmtAxisMoney, fmtAxisMoneyCompact } from './chartAxis'

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTH_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const utc = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return Date.UTC(y, m - 1, d || 1)
}
/** The house compactor (fmtMoney), except under £1,000, where it rounds a
 *  non-zero value to "£0" or "£1" — a figure the data contradicts; there the
 *  exact amount is printed instead. */
const compact = (pence: number) => (Math.abs(pence) < 100_000 ? money(pence / 100) : fmtMoney(pence))
const pct = (num: number, den: number) => (den > 0 ? Math.round((num / den) * 100) : 0)
const names = (items: { title: string }[], max = 3) => {
  const shown = items.slice(0, max).map(d => d.title)
  const more = items.length - shown.length
  return more > 0 ? `${shown.join(', ')} +${more}` : shown.join(', ')
}
const joinAnd = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`)

// ── Categorised share (spec §3.4 "Forecast confidence", NAME RULED §18.4) ─────

export type ShareLevel = 'high' | 'medium' | 'low'
export const SHARE_HIGH_PCT = 80
export const SHARE_MEDIUM_PCT = 40
export const LEVEL_WORD: Record<ShareLevel, string> = { high: 'High', medium: 'Medium', low: 'Low' }

export interface CategorisedShare {
  /** null when there is no open value to categorise (the share is undefined, not 0). */
  sharePct: number | null
  level: ShareLevel | null
  categorised: SalesFencedFigure
  open: SalesFencedFigure
  /** "4% of open value categorised" — or the empty statement. */
  line: string
  /** "£12.5M categorised of £1.2B open · 3 of 9 deals" with the fence tails. */
  figures: string
  guidance: string
}

/** Σ over the roll-up's buckets — the tenant-wide EFFECTIVE view, fenced per
 *  cell, exactly what "By forecast category" renders. '' = Uncategorised. */
function sumCells(cells: SalesForecast['rollup'], pick: (category: string) => boolean): SalesFencedFigure {
  return cells.filter(c => pick(c.category)).reduce<SalesFencedFigure>(
    (a, c) => ({ pence: a.pence + c.pence, count: a.count + c.count, excludedNonGbp: a.excludedNonGbp + (c.excludedNonGbp ?? 0) }),
    { pence: 0, count: 0, excludedNonGbp: 0 })
}

/**
 * Spec §3.4: share = Σ value of open deals WITH a category ÷ Σ value of all
 * open deals (Omitted is a category). Levels ≥ 80 High, ≥ 40 Medium, else Low.
 * GUIDANCE — narrowed where the spec's sentence would be false:
 *   - Low: the spec's "Most open value has no forecast category, so the Q4
 *     forecast is a guess" holds (under 40% categorised); "Q4" is dropped —
 *     the share is of ALL open value, not one quarter's.
 *   - Medium: "Most open value has no category" is FALSE between 50% and 80%,
 *     so Medium says "Part of the open value…".
 *   - Low / Medium, the spec's "Accept the suggestions to fix this in one
 *     step": ONE step would be Accept all, which is REFUSED (ADOPTION §18.6);
 *     the copy points at the suggestions, one deal at a time, and only when
 *     the wire carries suggestions.
 *   - High: "Every open deal has a forecast category" is said only when it is
 *     true (no Uncategorised deal at all, excluded included); otherwise
 *     "Most open value has a forecast category". "Next: set rep targets…" only
 *     while no rep has a target (totalTargetPence null).
 */
export function categorisedShare(f: SalesForecast): CategorisedShare {
  const open = sumCells(f.rollup, () => true)
  const categorised = sumCells(f.rollup, c => c !== '')
  const uncategorised = sumCells(f.rollup, c => c === '')
  const figures = `${money(categorised.pence / 100)} categorised of ${money(open.pence / 100)} open · `
    + `${categorised.count} of ${fencedDealCount(open.count, open.excludedNonGbp)}`
  if (open.pence <= 0) {
    return {
      sharePct: null, level: null, categorised, open, figures,
      line: open.count + open.excludedNonGbp === 0 ? 'No open deals to categorise' : 'No open value to categorise',
      guidance: 'The share is a share of open value, and there is none to measure.',
    }
  }
  const sharePct = pct(categorised.pence, open.pence)
  const level: ShareLevel = sharePct >= SHARE_HIGH_PCT ? 'high' : sharePct >= SHARE_MEDIUM_PCT ? 'medium' : 'low'
  const pending = (f.suggestions ?? []).length
  const accept = pending > 0 ? ' Accept the suggestions above, one deal at a time.' : ''
  let guidance: string
  if (level === 'low') guidance = `Most open value has no forecast category, so this forecast is mostly a guess.${accept}`
  else if (level === 'medium') guidance = `Part of the open value has no forecast category, so part of this forecast is a guess.${accept}`
  else {
    const every = uncategorised.count === 0 && uncategorised.excludedNonGbp === 0
    guidance = every ? 'Every open deal has a forecast category.' : 'Most open value has a forecast category.'
    if (f.totalTargetPence === null) guidance += ' Next: set rep targets so attainment can be measured.'
  }
  return { sharePct, level, categorised, open, figures, line: `${sharePct}% of open value categorised`, guidance }
}

// ── Attribution (spec §3.4; ALWAYS present, §18.5) ────────────────────────────

export interface AttributionSplit {
  headline: string
  /** The split bar's two widths, 0–100; both 0 when nothing was won. */
  attributedPct: number
  unattributedPct: number
  attributedCaption: string
  unattributedCaption: string
  /** The amber tile shows only while unattributed value exists (attention);
   *  at zero the card stays, the tile is neutral. */
  attention: boolean
}

/**
 * Headline, spec: "{unattributed} of {period} wins count for no one". At zero
 * unattributed the card STAYS (R1, §18.5) and the headline says what is true:
 * every win counts, or there are no wins yet. Widths are shares of the two
 * fenced halves' pence — the same two figures the captions print.
 */
export function attributionSplit(f: SalesForecast): AttributionSplit {
  const name = periodName(f.periodKind, f.periodStart)
  const a = f.attributedWonPence, u = f.unattributedWonPence
  const total = a + u
  const wonAny = f.attributedWonCount + f.unattributedWonCount > 0
  let headline: string
  if (u > 0) headline = `${compact(u)} of ${name} wins count for no one`
  else if (a > 0) headline = `Every ${name} win counts toward a target`
  else if (wonAny) headline = `${name} wins carry no captured value yet`
  else headline = `No ${name} wins yet`
  return {
    headline,
    attributedPct: total > 0 ? (a / total) * 100 : 0,
    unattributedPct: total > 0 ? (u / total) * 100 : 0,
    attributedCaption: `Linked ${money(a / 100)} · ${fencedDealCount(f.attributedWonCount, f.attributedWonExcludedNonGbp)}`,
    unattributedCaption: `Unlinked ${money(u / 100)} · ${fencedDealCount(f.unattributedWonCount, f.unattributedWonExcludedNonGbp)}`,
    attention: u > 0,
  }
}

// ── The look-ahead (spec §3.5 "Q4 look-ahead" — the NEXT period, from the wire) ─

export interface LookAheadBar {
  month: string // "2026-10"
  label: string // "Oct"
  pence: number
  count: number
  excludedNonGbp: number
  valueLabel: string // compact, the axis unit
  exact: string // the tooltip's exact figure
  /** "Oct · test, Rio Tinto" — up to 3 names then "+n"; "Oct · no deals". */
  xLabel: string
  heightPct: number // of the top tick
  empty: boolean
}

export interface LookAhead {
  title: string // "Q4 2026 look-ahead"
  subtitle: string
  name: string // "Q4 2026"
  total: string // fmtMoney of the wire's fenced total
  totalExact: string
  due: string // "7 deals due in Q4 2026" (+ excluded)
  /** True when the next period holds no open deal at all (fenced or not). */
  empty: boolean
  emptyStatement: string
  bars: LookAheadBar[]
  ticks: { value: number; label: string; pct: number }[]
  insight: string | null
  footnote: string | null
  fenceNote: string
}

/** The spec's first bar colours are position-bound shades of one hue; months
 *  are one SERIES (one measure over time), so they are drawn in one colour
 *  (the dataviz rule: a single series needs no second hue) — see tokens.ts
 *  `forecastLookAheadBar`. */
export function lookAhead(f: SalesForecast): LookAhead | null {
  const n = f.nextPeriod
  if (!n || !f.today) return null
  const name = periodName(n.periodKind, n.periodStart)
  const empty = n.count === 0 && n.excludedNonGbp === 0 && n.months.every(m => m.deals.length === 0)
  const max = Math.max(0, ...n.months.map(m => m.pence))
  const ticks = niceTicks(max, 4)
  const top = ticks[ticks.length - 1] || 1
  const unit = axisUnit(top / 100)
  const bars: LookAheadBar[] = n.months.map((m: SalesForecastMonth) => {
    const d = new Date(utc(m.month + '-01'))
    const label = MONTH_ABBR[d.getUTCMonth()]
    const has = m.deals.length > 0
    return {
      month: m.month, label, pence: m.pence, count: m.count, excludedNonGbp: m.excludedNonGbp,
      valueLabel: fmtAxisMoneyCompact(m.pence / 100, unit),
      exact: `${MONTH_FULL[d.getUTCMonth()]} ${d.getUTCFullYear()}: ${money(m.pence / 100)} · ${fencedDealCount(m.count, m.excludedNonGbp)}`,
      xLabel: has ? `${label} · ${names(m.deals)}` : `${label} · no deals`,
      heightPct: max > 0 ? (m.pence / top) * 100 : 0,
      empty: !has,
    }
  })
  return {
    title: `${name} look-ahead`,
    subtitle: 'Open pipeline by expected close month · unweighted',
    name,
    total: compact(n.pence),
    totalExact: money(n.pence / 100),
    due: `${fencedDealCount(n.count, n.excludedNonGbp)} due in ${name}`,
    empty,
    emptyStatement: `No open deal has an expected close date in ${name}.`,
    bars,
    ticks: ticks.map(t => ({ value: t, label: fmtAxisMoney(t / 100, unit), pct: (t / top) * 100 })),
    insight: empty ? null : lookAheadInsight(f, name),
    footnote: lookAheadFootnote(f),
    fenceNote: n.excludedNonGbp > 0 ? `${excludedSuffix(n.excludedNonGbp)} — non-GBP value with no captured rate is not in these figures` : '',
  }
}

/**
 * Spec §3.5's insight row:
 *   - one month holds ≥ 60% of the value → "{pct}% of {period} value closes in
 *     {Month}", and when EVERY deal of that month is at the first stage (New
 *     Lead) → "and all of it is still at New Lead. Qualify {top 2} in {first
 *     month} to de-risk the {quarter|month}."
 *   - otherwise the spec's "Q4 is spread evenly: m1 / m2 / m3" would be FALSE
 *     for e.g. 55 / 40 / 5, so it states the split without the adverb:
 *     "No month holds 60% of {period} value: Oct £x / Nov £y / Dec £z."
 * A next period with one month (a month view) has nothing to compare: null.
 * "top 2" = the month's first two deals in the WIRE's order (largest
 * effective amount first — the order every list inherits).
 */
export const INSIGHT_SHARE_PCT = 60
export function lookAheadInsight(f: SalesForecast, name: string): string | null {
  const n = f.nextPeriod!
  if (n.months.length < 2 || n.pence <= 0) return null
  const top = n.months.reduce((a, m) => (m.pence > a.pence ? m : a), n.months[0])
  const share = pct(top.pence, n.pence)
  const monthName = (m: { month: string }) => MONTH_FULL[new Date(utc(m.month + '-01')).getUTCMonth()]
  if ((top.pence / n.pence) * 100 >= INSIGHT_SHARE_PCT) {
    let s = `${share}% of ${name} value closes in ${monthName(top)}`
    const allFirst = top.deals.length > 0 && top.deals.every(d => d.stage === 'new_lead')
    if (allFirst) {
      const first = SLUG_TO_STAGE['new_lead'] ?? 'New Lead'
      const lead = joinAnd(top.deals.slice(0, 2).map(d => d.title))
      const unitWord = n.periodKind === 'quarter' ? 'quarter' : 'month'
      s += `, and all of it is still at ${first}. Qualify ${lead} in ${monthName(n.months[0])} to de-risk the ${unitWord}.`
    } else s += '.'
    return s
  }
  return `No month holds ${INSIGHT_SHARE_PCT}% of ${name} value: ${n.months.map(m => `${MONTH_ABBR[new Date(utc(m.month + '-01')).getUTCMonth()]} ${compact(m.pence)}`).join(' / ')}.`
}

/**
 * Spec §3.5's footnote: "Not shown: {no close date} and {close date passed}".
 * Undated deals are never in a month, so they are always "not shown". An
 * OVERDUE deal is not shown only when its date lies outside the next period:
 * viewing a PAST period, the next period can hold dates already passed, and
 * those deals ARE drawn in their month — the footnote must not claim
 * otherwise. Names only (the lists' items); no figure is summed here.
 */
export function lookAheadFootnote(f: SalesForecast): string | null {
  const n = f.nextPeriod!
  const start = utc(n.periodStart), end = utc(n.periodEnd)
  const undated = f.undated?.items ?? []
  const overdueOutside = (f.overdue?.items ?? []).filter(d => {
    if (!d.expectedClose) return true
    const t = utc(d.expectedClose)
    return t < start || t >= end
  })
  const overdueInside = (f.overdue?.items ?? []).length - overdueOutside.length
  const parts: string[] = []
  const phrase = (items: SalesForecastDealRef[], why: string) => `${dealCount(items.length)} with ${why} (${names(items)})`
  if (undated.length) parts.push(phrase(undated, 'no close date'))
  if (overdueOutside.length) parts.push(phrase(overdueOutside, 'a close date that has passed'))
  let s = parts.length ? `Not shown: ${joinAnd(parts)}.` : ''
  if (overdueInside > 0) s += `${s ? ' ' : ''}${dealCount(overdueInside)} dated inside ${periodName(n.periodKind, n.periodStart)} whose close date has passed ${overdueInside === 1 ? 'is' : 'are'} shown in ${overdueInside === 1 ? 'its' : 'their'} month.`
  return s || null
}

// ── By forecast category (spec §3.6, re-skinned) ──────────────────────────────

export interface ByCategoryHead {
  subtitle: string // "All 9 open deals · £12.5M"
  totalExact: string
}

/** "All {n} open deals · {total}" — n counts every open deal in the roll-up,
 *  the fence's excluded rows included (they are open deals); the total is the
 *  fenced sum, and the card's fence note says what it left out. */
export function byCategoryHead(f: SalesForecast): ByCategoryHead {
  const open = sumCells(f.rollup, () => true)
  const all = open.count + open.excludedNonGbp
  return {
    subtitle: `All ${all} open ${all === 1 ? 'deal' : 'deals'} · ${compact(open.pence)}`,
    totalExact: money(open.pence / 100),
  }
}

/** A roll-up row's share of open value, 0–100 — the spec's "6px bar showing
 *  the share of open value" (the pre-F4 bar was scaled to the largest bucket). */
export function shareOfOpen(cellPence: number, f: SalesForecast): number {
  const open = sumCells(f.rollup, () => true).pence
  return open > 0 ? (cellPence / open) * 100 : 0
}

